/**
 * Спільна логіка побудови колонок Kanban для всіх таблиць (Persons / Payments /
 * custom). Раніше дублювалась у трьох routes. Працює над `ApiRecord[]`
 * (значення вже зведені у плоский `values`).
 *
 * Підтримує групування по типах: dropdown/select, checkbox (Так/Ні), country,
 * tags (картка може потрапити у кілька колонок), date/datetime (бакети
 * Прострочено/Сьогодні/…). Числові типи та relation не групуються.
 */

import type { ApiField, ApiRecord } from './data-tables-api';
import { columnAccent, type ColumnAccent } from './kanban-colors';
import { parseTags } from './fields/format';
import countries from './countries.json';

export interface KanbanColumn {
  /** Стабільний ключ для {#each}. */
  key: string;
  label: string;
  color: ColumnAccent;
  records: ApiRecord[];
  /** Значення, яке встановити полю при drop у цю колонку.
   *  `undefined` → drop у цю колонку заборонено (read-only групування). */
  dropValue?: unknown;
}

const countryByCode: Record<string, { name: string; flag: string }> = Object.fromEntries(
  countries.map((c) => [c.code, { name: c.name, flag: c.flag }]),
);

/** Типи, по яких НЕ можна групувати (обчислювані / реляційні). */
const NON_GROUPABLE_TYPES = new Set(['linked_record', 'linked_summary', 'formula', 'stat']);

/** Типи, де drop між колонками встановлює значення поля (single-value, settable).
 *  tags/date — read-only групування (колонка не мапиться на одне settable-значення). */
const DRAG_SETTABLE_TYPES = new Set([
  'dropdown', 'checkbox', 'country', 'single_line', 'multi_line',
  'email', 'phone', 'url', 'number', 'currency', 'percent',
]);

export function isGroupableField(f: ApiField): boolean {
  if (f.hidden) return false;
  if (NON_GROUPABLE_TYPES.has(f.type)) return false;
  return true;
}

export function groupableFields(fields: ApiField[]): ApiField[] {
  return fields.filter(isGroupableField);
}

/** Чи тип поля підтримує переміщення карток drag-and-drop (set value on drop). */
export function fieldSupportsDrag(f: ApiField): boolean {
  return DRAG_SETTABLE_TYPES.has(f.type);
}

/* ── Date buckets ──────────────────────────────────────────────────────────── */

interface DateBucket {
  key: string;
  label: string;
  test: (d: Date, now: DateRefs) => boolean;
}
interface DateRefs {
  startToday: number;
  startTomorrow: number;
  startDayAfter: number;
  endWeek: number;
  endMonth: number;
}

const DATE_BUCKETS: DateBucket[] = [
  { key: 'overdue', label: 'Прострочено', test: (d, n) => d.getTime() < n.startToday },
  { key: 'today', label: 'Сьогодні', test: (d, n) => d.getTime() >= n.startToday && d.getTime() < n.startTomorrow },
  { key: 'tomorrow', label: 'Завтра', test: (d, n) => d.getTime() >= n.startTomorrow && d.getTime() < n.startDayAfter },
  { key: 'week', label: 'Цей тиждень', test: (d, n) => d.getTime() >= n.startDayAfter && d.getTime() < n.endWeek },
  { key: 'month', label: 'Цей місяць', test: (d, n) => d.getTime() >= n.endWeek && d.getTime() < n.endMonth },
  { key: 'later', label: 'Пізніше', test: (d, n) => d.getTime() >= n.endMonth },
];

function dateRefs(): DateRefs {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 86_400_000;
  const startTomorrow = startToday + DAY;
  const startDayAfter = startToday + 2 * DAY;
  // кінець тижня (понеділок-неділя): к-сть днів до неділі включно
  const dow = now.getDay() === 0 ? 7 : now.getDay(); // Mon=1..Sun=7
  const endWeek = startToday + (8 - dow) * DAY;
  const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();
  return { startToday, startTomorrow, startDayAfter, endWeek, endMonth };
}

/* ── Column builder ────────────────────────────────────────────────────────── */

/**
 * Побудувати колонки для kanban.
 * @param field  поле групування
 * @param records записи (вже з плоским `values`)
 * @param canDrag чи дозволено переміщення (роль + редагованість поля) — впливає
 *               лише на `dropValue`; групування рендериться у будь-якому разі.
 */
export function buildColumns(
  field: ApiField,
  records: ApiRecord[],
  canDrag: boolean,
): KanbanColumn[] {
  const key = field.fieldKey;
  const settable = canDrag && fieldSupportsDrag(field);

  if (field.type === 'tags') {
    const byTag = new Map<string, ApiRecord[]>();
    const noTag: ApiRecord[] = [];
    for (const rec of records) {
      const tags = parseTags(rec.values[key]);
      if (tags.length === 0) noTag.push(rec);
      else for (const t of tags) {
        if (!byTag.has(t)) byTag.set(t, []);
        byTag.get(t)!.push(rec);
      }
    }
    const cols: KanbanColumn[] = [...byTag.entries()].map(([t, recs]) => ({
      key: t,
      label: t,
      color: columnAccent(t),
      records: recs,
    }));
    if (noTag.length > 0) cols.push({ key: '__none__', label: 'Без тегів', color: columnAccent(null), records: noTag });
    return cols;
  }

  if (field.type === 'date' || field.type === 'datetime') {
    const refs = dateRefs();
    const buckets = new Map<string, ApiRecord[]>();
    const noDate: ApiRecord[] = [];
    for (const rec of records) {
      const raw = rec.values[key];
      if (raw == null || raw === '') { noDate.push(rec); continue; }
      const d = new Date(String(raw));
      if (Number.isNaN(d.getTime())) { noDate.push(rec); continue; }
      const b = DATE_BUCKETS.find((bk) => bk.test(d, refs));
      const bk = b?.key ?? 'later';
      if (!buckets.has(bk)) buckets.set(bk, []);
      buckets.get(bk)!.push(rec);
    }
    const cols: KanbanColumn[] = DATE_BUCKETS
      .filter((b) => buckets.has(b.key))
      .map((b) => ({ key: b.key, label: b.label, color: columnAccent(b.key === 'overdue' ? 'failed' : b.key === 'today' ? 'active' : b.label), records: buckets.get(b.key)! }));
    if (noDate.length > 0) cols.push({ key: '__none__', label: 'Без дати', color: columnAccent(null), records: noDate });
    return cols;
  }

  if (field.type === 'checkbox') {
    const yes: ApiRecord[] = [];
    const no: ApiRecord[] = [];
    for (const rec of records) (rec.values[key] ? yes : no).push(rec);
    const cols: KanbanColumn[] = [];
    cols.push({ key: 'true', label: 'Так', color: columnAccent('так'), records: yes, dropValue: settable ? true : undefined });
    cols.push({ key: 'false', label: 'Ні', color: columnAccent('ні'), records: no, dropValue: settable ? false : undefined });
    return cols;
  }

  // dropdown / select / country — група на унікальне значення.
  const options: string[] = field.options ?? [];
  const extra = new Set<string>();
  for (const rec of records) {
    const v = rec.values[key];
    if (v != null && v !== '' && !options.includes(String(v))) extra.add(String(v));
  }
  const labelOf = (val: string): string => {
    if (field.type === 'country') {
      const c = countryByCode[val.toUpperCase()];
      return c ? `${c.flag} ${c.name}` : val;
    }
    return val;
  };
  const cols: KanbanColumn[] = [...options, ...extra].map((val) => ({
    key: val,
    label: labelOf(val),
    color: columnAccent(val),
    records: records.filter((r) => String(r.values[key] ?? '') === val),
    dropValue: settable ? val : undefined,
  }));
  const nullRecs = records.filter((r) => r.values[key] == null || r.values[key] === '');
  if (nullRecs.length > 0) {
    cols.push({
      key: '__none__',
      label: 'Без значення',
      color: columnAccent(null),
      records: nullRecs,
      dropValue: settable ? null : undefined,
    });
  }
  return cols;
}
